import { useQuery, useQueryClient } from '@tanstack/react-query';

import BACKEND_URL from 'constants/backendUrl';
import License from 'core/models/license';
import { useSessionStore } from 'stores/session';
import { GET } from 'utils/http';

// Couche d'accès « état serveur » de l'abonnement (license) via TanStack Query.
// Remplace l'ancien morceau Redux `state.subscription` ({ license, alreadyFetched })
// + l'action SET_LICENSE et le `licenseFetcher` de SubscriptionProvider.
// Le cache (présence/staleTime) remplace l'ancien flag `alreadyFetched`, donc
// `subscriptionAlreadyFetchedSelector` n'a plus de hook dédié.

// La clé inclut l'id du candidat car l'endpoint est scopé par utilisateur :
// chaque utilisateur a donc sa propre entrée de cache.
export const subscriptionQueryKey = (candidateId: string | null | undefined) =>
    ['subscription', candidateId] as const;

// Endpoint historiquement utilisé par licenseFetcher (SubscriptionProvider) :
// GET /api/candidates/:candidateId/license.
// Reprend la transformation exacte : une réponse vide ({}) devient `null`.
export const fetchSubscription = async (candidateId: string): Promise<License | null> => {
    const response = (await GET(`${BACKEND_URL}/api/candidates/${candidateId}/license`)) as License;

    if (response && Object.keys(response).length > 0) {
        return response;
    }

    return null;
};

// Hook de requête de base (équivalent du licenseFetcher + state.subscription.license).
// Lit l'utilisateur courant depuis le store de session (état client) et n'exécute
// la requête que lorsqu'un id est disponible (gate équivalent à l'ancien
// `if (!candidatesAlreadyFetched)` côté provider).
export const useSubscriptionQuery = () => {
    const candidateId = useSessionStore((state) => state.user?.id ?? null);

    return useQuery({
        queryKey: subscriptionQueryKey(candidateId),
        queryFn: () => fetchSubscription(candidateId as string),
        enabled: Boolean(candidateId),
    });
};

// Équivalent de subscriptionSelector(state) -> useSubscription().
// Renvoie la license (License | null), comme l'ancien sélecteur.
export const useSubscription = (): License | null => useSubscriptionQuery().data ?? null;

// À appeler après une opération qui change la license côté serveur pour
// resynchroniser depuis le serveur. Remplace aussi subscriptionAlreadyFetchedSelector :
// le cache gère désormais le « déjà chargé ».
export const useInvalidateSubscription = () => {
    const queryClient = useQueryClient();

    return () => queryClient.invalidateQueries({ queryKey: ['subscription'] });
};

// --- Mutations -----------------------------------------------------------------
// Le domaine « subscription » n'expose aucune action d'écriture serveur : l'unique
// action Redux (SetLicense) ne faisait que stocker le résultat du GET ci-dessus.
// Aucun hook de mutation n'est donc nécessaire ici. L'allocation/révocation de
// license passe par d'autres domaines (cf. core/api/{agencies,candidates}.ts :
// useAllocateLicenseMutation, useAllocateLicensesMutation).
